// ImplementPipeline — the roadmap the autonomous "implement an issue" run
// renders while it works. A fixed stepper:
//
//   Boot → Understand → Implement → Verify → Open MR → Deploy review app
//
// On a GitHub repository the last step is "Open pull request" and there is no
// deploy step: GitHub has no review app, and its run goes from the PR straight
// to `done`.
//
// `boot` is driven by the sandbox phase (triggering / booting / ready); the rest
// come from the server's `implement_stage` events (folded into `useWsSession`'s
// `implement` snapshot). Pure derivation in `computeSteps` so it's unit-testable.

import type { ImplementStage } from "@loom/shared";
import type { Forge } from "@loom/shared/forge";
import type { SandboxStatusSnapshot } from "../state/sandbox-status-store";
import type { ImplementSnapshot } from "../chat/useWsSession";
import { useT, type MessageKey } from "../i18n/index.js";

import { IconCheck } from "./ui";

export type StepStatus = "pending" | "active" | "done" | "failed";

export interface PipelineStep {
  readonly key: ImplementStage;
  /** i18n catalog key for the step name — resolved to display text at render. */
  readonly label: MessageKey;
  readonly status: StepStatus;
  readonly detail: string | null;
}

const STEP_LABELS: ReadonlyArray<{ key: ImplementStage; label: MessageKey }> = [
  { key: "boot", label: "focus.pipeline.boot" },
  { key: "understand", label: "focus.pipeline.understand" },
  { key: "implement", label: "focus.pipeline.implement" },
  { key: "verify", label: "focus.pipeline.verify" },
  { key: "mr", label: "focus.pipeline.mr" },
  { key: "deploy", label: "focus.pipeline.deploy" },
];

// GitHub: the same steps up to the pull request, which is the last one.
const GITHUB_STEP_LABELS: ReadonlyArray<{ key: ImplementStage; label: MessageKey }> =
  STEP_LABELS.filter(({ key }) => key !== "deploy").map((s) =>
    s.key === "mr" ? { key: s.key, label: "focus.pipeline.pr" } : s,
  );

// Index of each real step. `done` collapses to "all done"; `failed` (the
// generic, continuation-cap failure before an MR exists) maps to the MR step.
// `deploy` is never reached on GitHub: no deploy frame is sent there.
const STEP_INDEX: Record<string, number> = {
  boot: 0,
  understand: 1,
  implement: 2,
  verify: 3,
  mr: 4,
  deploy: 5,
};

const TERMINAL_PHASES = new Set(["terminated"]);

/**
 * Derive the step states from the sandbox phase + the latest implement frame,
 * for the forge's step list. Exported for unit testing.
 */
export const computeSteps = (
  phase: SandboxStatusSnapshot["phase"],
  impl: ImplementSnapshot | null,
  forge: Forge = "gitlab",
): PipelineStep[] => {
  const steps = forge === "github" ? GITHUB_STEP_LABELS : STEP_LABELS;
  const ready = phase === "ready";
  const sandboxDead = TERMINAL_PHASES.has(phase);

  // Resolve the "current" index + whether we're in a failed / done terminal.
  let doneAll = false;
  let failedIdx: number | null = null;
  let activeIdx: number;
  let activeDetail: string | null = null;

  if (impl === null) {
    // Pre-implement: only the boot step has signal.
    activeIdx = 0;
    if (sandboxDead) failedIdx = 0;
  } else if (impl.stage === "done") {
    doneAll = true;
    activeIdx = steps.length;
    activeDetail = impl.detail;
  } else if (impl.status === "failed") {
    failedIdx = impl.stage === "failed" ? STEP_INDEX.mr! : STEP_INDEX[impl.stage]!;
    activeIdx = failedIdx;
    activeDetail = impl.detail;
  } else {
    activeIdx = STEP_INDEX[impl.stage] ?? 0;
    activeDetail = impl.detail;
  }

  // A sandbox death mid-run fails whatever step we were on.
  if (sandboxDead && failedIdx === null && !doneAll) {
    failedIdx = activeIdx;
  }

  return steps.map(({ key, label }, i): PipelineStep => {
    let status: StepStatus;
    if (failedIdx !== null) {
      status = i < failedIdx ? "done" : i === failedIdx ? "failed" : "pending";
    } else if (doneAll) {
      status = "done";
    } else if (i < activeIdx) {
      status = "done";
    } else if (i === activeIdx) {
      // Boot is "done" the moment the sandbox is ready even if no implement
      // frame has landed yet.
      if (key === "boot" && ready && (impl === null || activeIdx > 0)) {
        status = "done";
      } else {
        status = impl?.status === "done" ? "done" : "active";
      }
    } else {
      status = "pending";
    }
    const detail = status === "active" || status === "failed" ? activeDetail : null;
    return { key, label, status, detail };
  });
};

const StepIcon = ({ status, n }: { status: StepStatus; n: number }) => {
  if (status === "done") return <span className="impl-step-mark is-done"><IconCheck /></span>;
  if (status === "failed") return <span className="impl-step-mark is-failed">!</span>;
  if (status === "active")
    return <span className="impl-step-mark is-active"><span className="impl-spinner" /></span>;
  return <span className="impl-step-mark is-pending">{n}</span>;
};

export function ImplementPipeline({
  phase,
  implement,
  forge = "gitlab",
}: {
  readonly phase: SandboxStatusSnapshot["phase"];
  readonly implement: ImplementSnapshot | null;
  readonly forge?: Forge;
}) {
  const t = useT();
  const steps = computeSteps(phase, implement, forge);
  return (
    <ol className="impl-pipeline" aria-label={t("focus.pipeline.aria")}>
      {steps.map((step, i) => (
        <li key={step.key} className={`impl-step is-${step.status}`}>
          <StepIcon status={step.status} n={i + 1} />
          <div className="impl-step-body">
            <span className="impl-step-label">{t(step.label)}</span>
            {step.detail && <span className="impl-step-detail">{step.detail}</span>}
          </div>
        </li>
      ))}
    </ol>
  );
}
